import React, { useEffect, useMemo, useRef, useState } from 'react';
import invoiceService from '../services/invoiceService';
import { useT } from '../i18n/LanguageContext';
import {
  ACCOUNT_TYPES,
  isIntuitTokenUrl,
  validateBank,
  tokenize,
  tokenizationErrorMessage,
} from '../utils/intuitBankToken';
export {
  ACCOUNT_TYPES,
  INTUIT_TOKEN_HOSTS,
  isIntuitTokenUrl,
  validateBank,
  tokenBody,
  tokenize,
  TokenizeError,
} from '../utils/intuitBankToken';

/**
 * PAY INVOICES - BY BANK ACCOUNT (ACH) ONLY, inside the app.
 *
 * Card payments were removed on 9/3/26 (user decision). There is no method choice: the dialog IS
 * the bank form, `method: 'bank'` is the only value this file can send, and the server refuses
 * anything else with a 400. Do not add a card form back here.
 *
 * THE ACCOUNT NUMBER NEVER REACHES OUR SERVER. The form posts it straight to Intuit's tokens
 * endpoint (config.tokenUrl - and ONLY if that is an Intuit host, see isIntuitTokenUrl) and gets
 * a one-time token; only that token, the invoice ids, the total the payer saw and a name go to
 * /invoices/pay. Keep it that way: nothing in this file may send `bankAccount` anywhere but Intuit.
 *
 * What the payer sees: the invoices and the total, the bank form with its debit authorization,
 * then a receipt that says the debit settles in a few business days. A decline says so and leaves
 * them on the form to try another account. The rare "paid but QuickBooks not yet told" case is
 * shown as paid (it is) with a note that the record follows. The rarer "the processor did not
 * answer" case says NOT to try again - the server is finding out what happened.
 */

/** The one and only payment method. The request body's `method` is always this. */
export const PAYMENT_METHOD = 'bank';

/** Does the server's pay config allow paying here at all? Bank must be on; nothing else is looked at. */
export function bankPaymentsAvailable(config) {
  if (!config || !config.enabled || !isIntuitTokenUrl(config.tokenUrl)) return false;
  if (Array.isArray(config.methods) && !config.methods.includes(PAYMENT_METHOD)) return false;
  return true;
}

const money = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputClass =
  'w-full h-[44px] px-3 border border-[#d6dce8] rounded-[8px] text-sm text-[#0f172a] bg-white focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]';

const EMPTY_BANK = {
  name: '',
  routing: '',
  account: '',
  accountConfirm: '',
  accountType: 'BUSINESS_CHECKING',
  phone: '',
  authorized: false,
};

export default function PayInvoicesModal({
  invoices,
  clinicName,
  config,
  onClose,
  onPaid,
  onAmountChanged,
}) {
  const t = useT();
  const [bank, setBank] = useState(EMPTY_BANK);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);
  /** After an outcome the server could not confirm, the form must not be resubmitted. */
  const [halted, setHalted] = useState(false);
  /** After a 409 the amounts on screen are stale; the page has refreshed its list, this form is done. */
  const [stale, setStale] = useState(false);
  const busyRef = useRef(false);
  const firstFieldRef = useRef(null);

  const total = useMemo(
    () => Math.round(invoices.reduce((s, i) => s + Number(i.balance || 0), 0) * 100) / 100,
    [invoices]
  );
  const setB = (k, v) => {
    setBank((p) => ({ ...p, [k]: v }));
    setError('');
  };

  // The page learns whether the form was halted so it can drop a selection that must not be re-sent.
  const close = () => {
    if (!busyRef.current) onClose({ halted });
  };

  useEffect(() => {
    if (firstFieldRef.current) firstFieldRef.current.focus();
  }, [receipt]);

  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (busyRef.current || halted || stale) return;
    const problem = validateBank(bank);
    if (problem) {
      setError(t(problem));
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      const token = await tokenize(config.tokenUrl, bank);
      const body = {
        invoiceIds: invoices.map((i) => i._id),
        method: PAYMENT_METHOD,
        token,
        payerName: bank.name.trim(),
        expectedAmount: total,
      };
      const res = await invoiceService.pay(body);
      const r = res.data?.data || res.data;
      setReceipt(r);
      // The form's details are gone the moment we have a token; clear them from memory too.
      setBank(EMPTY_BANK);
      if (onPaid) onPaid(r);
    } catch (err) {
      const status = err.response?.status;
      // TokenizeError stores only allowlisted app-authored validation/code mappings.
      // Backend prose and transport exceptions never become payment guidance.
      const tokenMessage = tokenizationErrorMessage(err);
      if (tokenMessage) {
        // Nothing reached our server; the payer can correct the details and try again.
        setError(t(tokenMessage));
      } else if ((status >= 500 && status < 600) || !err?.response) {
        // The server could not confirm the outcome (or we never heard back from it). Money may have
        // moved; a second attempt is exactly the wrong move.
        setHalted(true);
        setError(
          t(
            'We did not get an answer about this payment. Do NOT try again - check the Paid view in a minute, or contact MedEffects.'
          )
        );
      } else if (status === 409) {
        // A conflict can mean stale figures or a prior payment requiring review. The page
        // refreshes its list; this form stops here without making a claim about charges.
        setStale(true);
        setError(
          t(
            'This payment needs review. Close this window and review the refreshed list, or contact MedEffects.'
          )
        );
        if (onAmountChanged) onAmountChanged();
      } else if (status === 429) {
        setError(
          t('Too many payment attempts in a short time. Please wait a minute and try again.')
        );
      } else {
        setError(t('The payment could not be processed.'));
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={t('Pay invoices')}
    >
      <div className="absolute inset-0 bg-black/60" onClick={close} />
      <div className="relative bg-white rounded-[14px] shadow-xl w-[560px] max-w-[calc(100vw-2rem)] z-10 max-h-[90vh] flex flex-col">
        <div className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px] px-5 py-4 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-[16px] font-semibold text-[#0f172a]">
              {receipt ? t('Payment received') : t('Pay {amount}', { amount: money(total) })}
            </h2>
            <p className="text-[12px] text-[#64748b] mt-0.5">
              {clinicName} ·{' '}
              {t(invoices.length === 1 ? '{n} invoice' : '{n} invoices', { n: invoices.length })}
              {config?.environment === 'sandbox' ? ` · ${t('TEST MODE (sandbox)')}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={busy}
            aria-label={t('Close')}
            className="w-6 h-6 flex items-center justify-center text-[#64748b] hover:text-[#0f172a] shrink-0"
          >
            <span className="material-symbols-outlined text-[24px]">cancel</span>
          </button>
        </div>

        {receipt ? (
          <div className="px-5 py-5 overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className="material-symbols-outlined text-[36px] text-[#007a55]">
                check_circle
              </span>
              <div>
                <p className="text-[15px] font-semibold text-[#0f172a]">
                  {t('{amount} paid', { amount: money(receipt.amount) })}
                </p>
                <p className="text-[12px] text-[#64748b]">
                  {t('Bank account')}
                  {receipt.instrument?.brand ? ` · ${receipt.instrument.brand}` : ''}
                  {receipt.instrument?.last4
                    ? ` ${t('ending {last4}', { last4: receipt.instrument.last4 })}`
                    : ''}
                  {receipt.intuitTxnId ? ` · ${t('ref {id}', { id: receipt.intuitTxnId })}` : ''}
                </p>
              </div>
            </div>
            <ul className="text-[13px] text-[#0f172a] divide-y divide-[#e2e8f0] border border-[#e2e8f0] rounded-[8px] mb-4">
              {(receipt.invoices || []).map((i) => (
                <li key={i.qbInvoiceId} className="flex justify-between px-3 py-2">
                  <span>{t('Invoice {number}', { number: i.docNumber || i.qbInvoiceId })}</span>
                  <span className="font-semibold">{money(i.amount)}</span>
                </li>
              ))}
            </ul>
            <p className="text-[12px] text-[#64748b] mb-2">
              {t('Bank payments settle in a few business days. The invoices show as paid now.')}
            </p>
            {receipt.recordingPending && (
              <p
                className="text-[12px] text-[#b45309] bg-[#fffbeb] border border-[#fde68a] rounded-[8px] px-3 py-2 mb-2"
                role="status"
              >
                {t(
                  'Your payment went through. QuickBooks will show it shortly - our team has been notified to finish the record.'
                )}
              </p>
            )}
            <div className="flex justify-end mt-4">
              <button
                type="button"
                ref={firstFieldRef}
                onClick={close}
                className="h-[40px] px-5 bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6]"
              >
                {t('Done')}
              </button>
            </div>
          </div>
        ) : (
          <form
            className="px-5 py-4 overflow-y-auto space-y-4"
            onSubmit={submit}
            autoComplete="off"
          >
            <ul className="text-[12px] text-[#0f172a] divide-y divide-[#e2e8f0] border border-[#e2e8f0] rounded-[8px] max-h-[140px] overflow-y-auto">
              {invoices.map((i) => (
                <li key={i._id} className="flex justify-between px-3 py-1.5">
                  <span>
                    {t('Invoice {number}', { number: i.docNumber || i.orderId || i.qbInvoiceId })}
                    {/* A QuickBooks due date is a calendar day stored as UTC midnight - format it in UTC, day for day with QuickBooks. */}
                    {i.dueDate ? (
                      <span className="text-[#64748b]">
                        {' '}
                        ·{' '}
                        {t('due {date}', {
                          date: new Date(i.dueDate).toLocaleDateString('en-US', {
                            timeZone: 'UTC',
                          }),
                        })}
                      </span>
                    ) : null}
                  </span>
                  <span className="font-semibold">{money(i.balance)}</span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-2 text-[13px] font-semibold text-[#0f172a]">
              <span className="material-symbols-outlined text-[18px] text-[#64748b]">
                account_balance
              </span>
              {t('Pay by bank account (ACH)')}
            </div>

            <div className="space-y-3">
              <div>
                <label
                  htmlFor="pay-bank-name"
                  className="block text-xs font-medium text-[#64748b] mb-1.5"
                >
                  {t('Name on account')}
                </label>
                <input
                  id="pay-bank-name"
                  ref={firstFieldRef}
                  className={inputClass}
                  value={bank.name}
                  onChange={(e) => setB('name', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="pay-routing"
                    className="block text-xs font-medium text-[#64748b] mb-1.5"
                  >
                    {t('Routing number')}
                  </label>
                  <input
                    id="pay-routing"
                    className={inputClass}
                    inputMode="numeric"
                    value={bank.routing}
                    onChange={(e) => setB('routing', e.target.value)}
                  />
                </div>
                <div>
                  <label
                    htmlFor="pay-type"
                    className="block text-xs font-medium text-[#64748b] mb-1.5"
                  >
                    {t('Account type')}
                  </label>
                  <select
                    id="pay-type"
                    className={inputClass}
                    value={bank.accountType}
                    onChange={(e) => setB('accountType', e.target.value)}
                  >
                    {ACCOUNT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {t(type.label)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="pay-account"
                    className="block text-xs font-medium text-[#64748b] mb-1.5"
                  >
                    {t('Account number')}
                  </label>
                  <input
                    id="pay-account"
                    className={inputClass}
                    inputMode="numeric"
                    value={bank.account}
                    onChange={(e) => setB('account', e.target.value)}
                  />
                </div>
                <div>
                  <label
                    htmlFor="pay-account2"
                    className="block text-xs font-medium text-[#64748b] mb-1.5"
                  >
                    {t('Confirm account number')}
                  </label>
                  <input
                    id="pay-account2"
                    className={inputClass}
                    inputMode="numeric"
                    value={bank.accountConfirm}
                    onChange={(e) => setB('accountConfirm', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="pay-phone"
                  className="block text-xs font-medium text-[#64748b] mb-1.5"
                >
                  {t('Phone')}
                </label>
                <input
                  id="pay-phone"
                  className={inputClass}
                  inputMode="tel"
                  value={bank.phone}
                  onChange={(e) => setB('phone', e.target.value)}
                />
              </div>
              <label className="flex items-start gap-2 text-[11px] text-[#64748b] cursor-pointer">
                <input
                  type="checkbox"
                  checked={bank.authorized}
                  onChange={(e) => setB('authorized', e.target.checked)}
                  aria-label={t('Authorize the debit')}
                  className="mt-0.5"
                />
                <span>
                  {t(
                    'I authorize MedEffects to debit this account for {amount} today. Bank payments settle in a few business days; a debit the bank returns leaves the invoices unpaid.',
                    { amount: money(total) }
                  )}
                </span>
              </label>
            </div>

            {!!error && (
              <p className="text-[12px] text-red-600" role="alert">
                {error}
              </p>
            )}

            <div className="flex items-center justify-between pt-1">
              <p className="text-[11px] text-[#94a3b8] flex items-center gap-1">
                <span className="material-symbols-outlined text-[14px]">lock</span>
                {t(
                  'Details go straight to Intuit (QuickBooks Payments). MedEffects never stores account numbers.'
                )}
              </p>
              <button
                type="submit"
                disabled={busy || halted || stale}
                className="h-[40px] px-5 bg-[#007a55] text-white text-sm font-semibold rounded-[10px] hover:bg-[#00694a] disabled:opacity-50 shrink-0"
              >
                {busy ? t('Processing...') : t('Pay {amount}', { amount: money(total) })}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
